import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { useDevicesStore } from "../../shared/store/devices.store";
import { DevicesService } from "../../shared/services/devices.service";
import { ButtonLarge, ButtonLink, PopUp } from "../../shared/uikit";

type ConfirmationAction = "logout" | "remove" | null;

export const DeviceDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const deviceId = Number(params.id);
  const store = useDevicesStore();
  // Если store ещё пуст (например, после перезапуска приложения или прямого
  // перехода на страницу), выполняем fallback-загрузку списка устройств с API.
  const device = store.devices.find((d) => d.id === deviceId);
  const [isFallbackLoading, setIsFallbackLoading] = useState(false);
  // Guard: предотвращает повторные fallback-запросы, если устройство
  // не существует на сервере (иначе useEffect зациклится).
  const hasAttemptedFallback = useRef(false);

  const [confirmAction, setConfirmAction] = useState<ConfirmationAction>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const refreshDevicesStore = useCallback(async () => {
    try {
      const devices = await DevicesService.getDevices();
      const { setDevices } = useDevicesStore.getState();
      setDevices(devices);
    } catch {
      // Silently fail — the store keeps the previous state
    }
  }, []);

  // Fallback: если устройство не найдено в store (прямой переход на страницу,
  // перезапуск приложения), загружаем актуальный список устройств с API.
  useEffect(() => {
    if (!Number.isNaN(deviceId) && !device && !hasAttemptedFallback.current) {
      hasAttemptedFallback.current = true;
      setIsFallbackLoading(true);
      refreshDevicesStore().finally(() => setIsFallbackLoading(false));
    }
  }, [deviceId, device, refreshDevicesStore]);

  const handleLogout = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await DevicesService.logoutDevice(deviceId);
      setActionSuccess(result.message || "Device logged out successfully");
      setConfirmAction(null);
      await refreshDevicesStore();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to logout device",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await DevicesService.removeDevice(deviceId);
      setActionSuccess(result.message || "Device access removed successfully");
      setConfirmAction(null);
      await refreshDevicesStore();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to remove device",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Некорректный ID в URL (например, /devices/abc или отсутствующий сегмент)
  if (Number.isNaN(deviceId)) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Device Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>❓</p>
          <p style={{ color: "var(--second-color)" }}>Invalid device ID</p>
        </div>
        <ButtonLarge
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Fallback-загрузка данных с сервера
  if (isFallbackLoading) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Device Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "var(--second-color)",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>⏳</p>
          <p>Loading device...</p>
        </div>
      </div>
    );
  }

  // Success state — выводится до проверки наличия устройства, чтобы после
  // logout/remove (когда устройство исчезает из store) пользователь увидел
  // подтверждение действия, а не "Device not found".
  if (actionSuccess) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Device Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>✅</p>
          <p style={{ color: "#28a745", fontWeight: 600 }}>{actionSuccess}</p>
        </div>
        <ButtonLarge
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
          mode="black"
        />
      </div>
    );
  }

  // Device not found
  if (!device) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Device Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔍</p>
          <p style={{ color: "var(--second-color)" }}>
            Device not found (ID: {deviceId})
          </p>
        </div>
        <ButtonLarge
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Main device detail view
  return (
    <div style={{ padding: "1rem" }}>
      <h2>Device Details</h2>

      {/* Device info card */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1.5rem",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-md)",
          background: "var(--second-background)",
        }}
      >
        {/* Device name */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: "1rem",
          }}
        >
          <span style={{ fontSize: "28px" }}>🖥</span>
          <h3 style={{ margin: 0 }}>
            {device.device_info?.name || `Device #${device.id}`}
          </h3>
        </div>

        {/* Status badge */}
        <div
          style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "var(--radius-sm)",
            fontSize: "13px",
            fontWeight: 600,
            background: device.is_active
              ? "var(--color-success)"
              : "var(--color-bg-secondary)",
            color: device.is_active ? "#fff" : "var(--second-color)",
            border: device.is_active ? "none" : "1px solid var(--color-border)",
            marginBottom: "1rem",
          }}
        >
          {device.is_active ? "🟢 Online" : "⚪ Offline"}
        </div>

        {/* Details */}
        <table style={{ width: "100%", fontSize: "14px", marginTop: "0.5rem" }}>
          <tbody>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                ID
              </td>
              <td style={{ padding: "0.5rem" }}>{device.id}</td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                OS
              </td>
              <td style={{ padding: "0.5rem" }}>
                {device.device_info?.os || "Unknown"}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Platform
              </td>
              <td style={{ padding: "0.5rem" }}>
                {device.device_info?.platform || "Unknown"}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                MAC Address
              </td>
              <td style={{ padding: "0.5rem" }}>
                {device.device_info?.mac_address || "Unknown"}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Processor ID
              </td>
              <td
                style={{
                  padding: "0.5rem",
                  fontSize: "12px",
                  wordBreak: "break-all",
                }}
              >
                {device.device_info?.processor_id || "Unknown"}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Session
              </td>
              <td style={{ padding: "0.5rem" }}>
                {device.session_id ? "🟢 Active" : "⚪ Expired"}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Created
              </td>
              <td style={{ padding: "0.5rem" }}>
                {formatDate(device.created_at)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Action error */}
      {actionError && (
        <p
          style={{
            color: "var(--accent-color)",
            fontSize: "14px",
            marginTop: "0.5rem",
            textAlign: "center",
          }}
        >
          ❌ {actionError}
        </p>
      )}

      {/* Danger Zone */}
      <div
        style={{
          marginTop: "1.5rem",
          padding: "1.25rem",
          border: "1px solid var(--color-danger)",
          borderRadius: "var(--radius-md)",
          background: "var(--first-background)",
        }}
      >
        <h3 style={{ color: "var(--color-danger)", margin: "0 0 0.5rem" }}>
          ⚠️ Danger Zone
        </h3>
        <p
          style={{
            fontSize: "13px",
            color: "var(--second-color)",
            margin: "0 0 1rem",
          }}
        >
          These actions are irreversible.
        </p>

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <ButtonLarge
            title="🚪 Logout Device"
            onClick={() => {
              setConfirmAction("logout");
              setActionError(null);
            }}
            mode="white"
            isSmall={true}
          />
          <ButtonLarge
            title="🗑 Remove Device"
            onClick={() => {
              setConfirmAction("remove");
              setActionError(null);
            }}
            mode="black"
            Accent={true}
            isSmall={true}
          />
        </div>
      </div>

      {/* Confirmation dialog */}
      {confirmAction && !actionLoading && (
        <PopUp
          content={
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "32px", marginBottom: "0.5rem" }}>
                {confirmAction === "logout" ? "🚪" : "🗑"}
              </p>
              <h3 style={{ margin: "0 0 0.75rem" }}>
                {confirmAction === "logout"
                  ? "Logout Device?"
                  : "Remove Device?"}
              </h3>
              <p
                style={{
                  color: "var(--second-color)",
                  fontSize: "14px",
                  margin: "0 0 1.25rem",
                }}
              >
                {confirmAction === "logout"
                  ? `This will end the session for "${device.device_info?.name || `Device #${device.id}`}".`
                  : `This will permanently remove access for "${device.device_info?.name || `Device #${device.id}`}".`}
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "center",
                }}
              >
                <ButtonLarge
                  title="Cancel"
                  onClick={() => setConfirmAction(null)}
                  mode="white"
                  isSmall={true}
                />
                <ButtonLarge
                  title={confirmAction === "logout" ? "🚪 Logout" : "🗑 Remove"}
                  onClick={
                    confirmAction === "logout" ? handleLogout : handleRemove
                  }
                  mode="black"
                  Accent={true}
                  isSmall={true}
                />
              </div>
            </div>
          }
          isCloseButton={true}
          onClick={() => setConfirmAction(null)}
        />
      )}

      {/* Loading state for confirmation */}
      {confirmAction && actionLoading && (
        <PopUp
          content={
            <div style={{ textAlign: "center" }}>
              <p style={{ color: "var(--second-color)" }}>⏳ Processing...</p>
            </div>
          }
          onClick={() => {}}
        />
      )}

      {/* Back navigation */}
      <ButtonLink
        title="← Back to Devices"
        onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
      />
    </div>
  );
};
